"use client";

// PlayerProvider owns the single <audio> element; UI surfaces read state via context.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Loader2, Pause, Play, RotateCcw, RotateCw, X } from "lucide-react";

import { ANALYTICS_EVENTS, track } from "@/lib/analytics";
import {
  formatDuration,
  type PodcastEpisode,
  type PodcastSeason,
} from "@/lib/podcast";
import { useRovingFocus } from "@/lib/use-roving-focus";

import { PodcastLogoTile } from "./podcast-logo-tile";

type PlayerState = {
  episodeId: string | null;
  episode: PodcastEpisode | null;
  seasonNumber: number | null;
  playing: boolean;
  loading: boolean;
  currentTime: number;
  duration: number;
};

type PlayerActions = {
  toggle: (episode: PodcastEpisode, seasonNumber: number | null) => void;
  seek: (sec: number) => void;
  seekBy: (delta: number) => void;
  stop: () => void;
  playPrev: () => void;
  playNext: () => void;
  playSeason: (seasonNumber: number) => void;
};

type PlayerLibrary = {
  seasons: PodcastSeason[];
  hasPrev: boolean;
  hasNext: boolean;
};

const initial: PlayerState = {
  episodeId: null,
  episode: null,
  seasonNumber: null,
  playing: false,
  loading: false,
  currentTime: 0,
  duration: 0,
};

const PlayerStateContext = createContext<PlayerState>(initial);
const PlayerActionsContext = createContext<PlayerActions | null>(null);
const PlayerLibraryContext = createContext<PlayerLibrary>({
  seasons: [],
  hasPrev: false,
  hasNext: false,
});

export function usePlayerState() {
  return useContext(PlayerStateContext);
}

export function usePlayerActions(): PlayerActions {
  const ctx = useContext(PlayerActionsContext);
  if (!ctx) throw new Error("usePlayerActions must be used inside PlayerProvider");
  return ctx;
}

export function usePlayerLibrary(): PlayerLibrary {
  return useContext(PlayerLibraryContext);
}

// Specials (no season) are excluded.
function flattenChronological(seasons: PodcastSeason[]): PodcastEpisode[] {
  const sorted = [...seasons].sort((a, b) => a.number - b.number);
  const out: PodcastEpisode[] = [];
  for (const s of sorted) {
    const eps = [...s.episodes].sort(
      (a, b) => (a.number ?? 0) - (b.number ?? 0),
    );
    out.push(...eps);
  }
  return out;
}

export function PlayerProvider({
  children,
  seasons = [],
}: {
  children: React.ReactNode;
  seasons?: PodcastSeason[];
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [state, setState] = useState<PlayerState>(initial);

  const flatEpisodes = useMemo(
    () => flattenChronological(seasons),
    [seasons],
  );

  // `playing` is the audio-frames event, distinct from `play` (call ack).
  const attachAudio = useCallback((node: HTMLAudioElement | null) => {
    audioRef.current = node;
    if (!node) return;
    const onTime = () =>
      setState((s) => ({ ...s, currentTime: node.currentTime }));
    const onMeta = () =>
      setState((s) => ({
        ...s,
        duration: Number.isFinite(node.duration) ? node.duration : s.duration,
      }));
    const onPlay = () => setState((s) => ({ ...s, playing: true }));
    const onPause = () =>
      setState((s) => ({ ...s, playing: false, loading: false }));
    const onPlaying = () =>
      setState((s) => ({ ...s, playing: true, loading: false }));
    const onWaiting = () => setState((s) => ({ ...s, loading: true }));
    const onCanPlay = () => setState((s) => ({ ...s, loading: false }));
    const onEnd = () =>
      setState((s) => ({
        ...s,
        playing: false,
        loading: false,
        currentTime: s.duration,
      }));
    node.addEventListener("timeupdate", onTime);
    node.addEventListener("loadedmetadata", onMeta);
    node.addEventListener("play", onPlay);
    node.addEventListener("playing", onPlaying);
    node.addEventListener("pause", onPause);
    node.addEventListener("waiting", onWaiting);
    node.addEventListener("canplay", onCanPlay);
    node.addEventListener("ended", onEnd);
  }, []);

  const toggle = useCallback<PlayerActions["toggle"]>(
    (episode, seasonNumber) => {
      const audio = audioRef.current;
      if (!audio) return;
      if (state.episodeId === episode.id) {
        if (audio.paused) audio.play().catch(() => {});
        else audio.pause();
        return;
      }
      // Optimistically flip loading=true so every UI surface shows the spinner.
      setState({
        episodeId: episode.id,
        episode,
        seasonNumber,
        playing: false,
        loading: true,
        currentTime: 0,
        duration: episode.duration || 0,
      });
      audio.src = episode.audioUrl;
      // queueMicrotask lets React commit the new src to avoid a first-load race.
      queueMicrotask(() => {
        audio.currentTime = 0;
        audio.play().catch(() => {});
      });
    },
    [state.episodeId],
  );

  const seek = useCallback<PlayerActions["seek"]>((sec) => {
    const audio = audioRef.current;
    if (!audio) return;
    const clamped = Math.max(
      0,
      Math.min(sec, Number.isFinite(audio.duration) ? audio.duration : sec),
    );
    audio.currentTime = clamped;
    setState((s) => ({ ...s, currentTime: clamped }));
  }, []);

  const seekBy = useCallback<PlayerActions["seekBy"]>(
    (delta) => {
      const audio = audioRef.current;
      if (!audio) return;
      seek(audio.currentTime + delta);
    },
    [seek],
  );

  const stop = useCallback<PlayerActions["stop"]>(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
    }
    setState(initial);
  }, []);

  const loadAndPlay = useCallback(
    (episode: PodcastEpisode, seasonNumber: number | null) => {
      const audio = audioRef.current;
      if (!audio) return;
      track(ANALYTICS_EVENTS.PODCAST_PLAYED, {
        episode: episode.title,
        season: seasonNumber ?? undefined,
      });
      setState({
        episodeId: episode.id,
        episode,
        seasonNumber,
        playing: false,
        loading: true,
        currentTime: 0,
        duration: episode.duration || 0,
      });
      audio.src = episode.audioUrl;
      queueMicrotask(() => {
        audio.currentTime = 0;
        audio.play().catch(() => {});
      });
    },
    [],
  );

  const playPrev = useCallback<PlayerActions["playPrev"]>(() => {
    if (!state.episodeId || flatEpisodes.length === 0) return;
    const i = flatEpisodes.findIndex((e) => e.id === state.episodeId);
    if (i <= 0) return;
    const prev = flatEpisodes[i - 1];
    loadAndPlay(prev, prev.season);
  }, [state.episodeId, flatEpisodes, loadAndPlay]);

  const playNext = useCallback<PlayerActions["playNext"]>(() => {
    if (!state.episodeId || flatEpisodes.length === 0) return;
    const i = flatEpisodes.findIndex((e) => e.id === state.episodeId);
    if (i < 0 || i >= flatEpisodes.length - 1) return;
    const next = flatEpisodes[i + 1];
    loadAndPlay(next, next.season);
  }, [state.episodeId, flatEpisodes, loadAndPlay]);

  const playSeason = useCallback<PlayerActions["playSeason"]>(
    (seasonNumber) => {
      const season = seasons.find((s) => s.number === seasonNumber);
      if (!season) return;
      if (state.seasonNumber === seasonNumber && state.episode) {
        toggle(state.episode, seasonNumber);
        return;
      }
      const firstEpisode =
        [...season.episodes].sort(
          (a, b) => (a.number ?? 0) - (b.number ?? 0),
        )[0] ?? null;
      if (firstEpisode) loadAndPlay(firstEpisode, seasonNumber);
    },
    [seasons, state.seasonNumber, state.episode, toggle, loadAndPlay],
  );

  const actions = useMemo(
    () => ({ toggle, seek, seekBy, stop, playPrev, playNext, playSeason }),
    [toggle, seek, seekBy, stop, playPrev, playNext, playSeason],
  );

  const library = useMemo<PlayerLibrary>(() => {
    if (!state.episodeId || flatEpisodes.length === 0) {
      return { seasons, hasPrev: false, hasNext: false };
    }
    const i = flatEpisodes.findIndex((e) => e.id === state.episodeId);
    return {
      seasons,
      hasPrev: i > 0,
      hasNext: i >= 0 && i < flatEpisodes.length - 1,
    };
  }, [seasons, flatEpisodes, state.episodeId]);

  return (
    <PlayerStateContext.Provider value={state}>
      <PlayerActionsContext.Provider value={actions}>
        <PlayerLibraryContext.Provider value={library}>
          {children}
          <audio ref={attachAudio} preload="metadata" />
        </PlayerLibraryContext.Provider>
      </PlayerActionsContext.Provider>
    </PlayerStateContext.Provider>
  );
}

export function EpisodePlayer({
  episode,
  seasonNumber,
}: {
  episode: PodcastEpisode;
  seasonNumber: number | null;
}) {
  const state = usePlayerState();
  const actions = usePlayerActions();
  const isCurrent = state.episodeId === episode.id;
  const playing = isCurrent && state.playing;
  const loading = isCurrent && state.loading;
  // Fall back to RSS duration until the audio's metadata loads.
  const duration =
    (isCurrent && state.duration > 0 ? state.duration : episode.duration) || 0;
  const currentTime = isCurrent ? state.currentTime : 0;
  const pct = duration > 0 ? (currentTime / duration) * 100 : 0;

  const trackRef = useRef<HTMLDivElement | null>(null);
  const { ref: groupRef, rovingProps } = useRovingFocus<HTMLDivElement>();
  const onTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isCurrent) actions.toggle(episode, seasonNumber);
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || duration <= 0) return;
    const ratio = (e.clientX - rect.left) / rect.width;
    actions.seek(Math.max(0, Math.min(1, ratio)) * duration);
  };

  return (
    <div
      ref={groupRef}
      role="group"
      aria-label={`Player for ${episode.title}`}
      className="flex flex-wrap items-center gap-3 sm:flex-nowrap"
      {...rovingProps}
    >
      <button
        type="button"
        data-roving=""
        onClick={() => actions.toggle(episode, seasonNumber)}
        aria-label={
          loading ? "Loading episode" : playing ? "Pause episode" : "Play episode"
        }
        aria-busy={loading || undefined}
        className="bg-purple-03 hover:bg-purple-04 flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full text-white transition-colors"
      >
        {loading ? (
          <Loader2 className="size-4 animate-spin" />
        ) : playing ? (
          <Pause className="size-4" />
        ) : (
          <Play className="ml-0.5 size-4" />
        )}
      </button>

      <span className="text-button shrink-0 font-mono tabular-nums text-black/65">
        {formatDuration(currentTime)} / {formatDuration(duration)}
      </span>

      <div
        ref={trackRef}
        data-roving=""
        onClick={onTrackClick}
        role="slider"
        aria-label={`Scrubber for ${episode.title}`}
        aria-valuemin={0}
        aria-valuemax={Math.max(1, Math.round(duration))}
        aria-valuenow={Math.round(currentTime)}
        className="group bg-dusk-02 relative h-2 min-w-[120px] flex-1 cursor-pointer rounded-full"
      >
        <div
          className="bg-purple-03 absolute inset-y-0 left-0 rounded-full"
          style={{ width: `${pct}%` }}
        />
        <div
          className="bg-purple-03 absolute top-1/2 size-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full opacity-0 shadow transition-opacity group-hover:opacity-100"
          style={{ left: `${pct}%` }}
        />
      </div>

      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          {...(isCurrent ? { "data-roving": "" } : {})}
          onClick={() => actions.seekBy(-15)}
          aria-label="Rewind 15 seconds"
          disabled={!isCurrent}
          className="text-purple-03 hover:bg-dusk-02 flex size-9 cursor-pointer items-center justify-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-30"
        >
          <RotateCcw className="size-4" />
        </button>
        <button
          type="button"
          {...(isCurrent ? { "data-roving": "" } : {})}
          onClick={() => actions.seekBy(30)}
          aria-label="Forward 30 seconds"
          disabled={!isCurrent}
          className="text-purple-03 hover:bg-dusk-02 flex size-9 cursor-pointer items-center justify-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-30"
        >
          <RotateCw className="size-4" />
        </button>
      </div>
    </div>
  );
}

export function StickyNowPlaying() {
  const state = usePlayerState();
  const actions = usePlayerActions();
  const duration = state.duration > 0 ? state.duration : state.episode?.duration ?? 0;
  const pct = duration > 0 ? (state.currentTime / duration) * 100 : 0;
  const trackRef = useRef<HTMLDivElement | null>(null);

  if (!state.episode) {
    return null;
  }

  const onTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || duration <= 0) return;
    const ratio = (e.clientX - rect.left) / rect.width;
    actions.seek(Math.max(0, Math.min(1, ratio)) * duration);
  };

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40">
      <div
        role="region"
        aria-label="Now playing"
        className="pointer-events-auto mx-auto flex w-full max-w-[1280px] flex-col gap-3 border-t border-black/5 bg-white px-4 py-3 shadow-[0_-12px_30px_-12px_rgba(20,10,60,0.18)] sm:px-6 md:flex-row md:items-center md:gap-5 md:rounded-t-[20px] md:border md:border-b-0"
      >
        <div className="flex min-w-0 items-center gap-3 md:min-w-[260px]">
          <PodcastLogoTile
            seasonNumber={state.seasonNumber}
            size="sm"
            playing={state.playing}
          />
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-purple-03">
              Fertility Talks · S{state.seasonNumber ?? "—"} · Ep {state.episode.number ?? "?"}
            </div>
            <div className="truncate text-button font-bold text-purple-05">
              {state.episode.title}
            </div>
          </div>
        </div>

        <div className="flex flex-1 items-center gap-3">
          <button
            type="button"
            onClick={() => actions.toggle(state.episode!, state.seasonNumber)}
            aria-label={
              state.loading ? "Loading" : state.playing ? "Pause" : "Play"
            }
            aria-busy={state.loading || undefined}
            className="bg-purple-03 hover:bg-purple-04 flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full text-white transition-colors"
          >
            {state.loading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : state.playing ? (
              <Pause className="size-4" />
            ) : (
              <Play className="ml-0.5 size-4" />
            )}
          </button>
          <span className="text-button shrink-0 font-mono tabular-nums text-black/65">
            {formatDuration(state.currentTime)} / {formatDuration(duration)}
          </span>
          <div
            ref={trackRef}
            onClick={onTrackClick}
            role="slider"
            tabIndex={0}
            aria-valuemin={0}
            aria-valuemax={Math.max(1, Math.round(duration))}
            aria-valuenow={Math.round(state.currentTime)}
            aria-label="Now-playing scrubber"
            className="bg-dusk-02 relative h-2 flex-1 cursor-pointer rounded-full"
          >
            <div
              className="bg-purple-03 absolute inset-y-0 left-0 rounded-full"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        <button
          type="button"
          onClick={() => actions.stop()}
          aria-label="Close player"
          className="text-black/50 hover:text-black hover:bg-dusk-02 flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}

export function useToggleEpisode() {
  const actions = usePlayerActions();
  return actions.toggle;
}

// Media Session API: system lock-screen controls metadata.
export function useMediaSessionMetadata() {
  const state = usePlayerState();
  useEffect(() => {
    if (!state.episode || typeof window === "undefined") return;
    const ms = navigator.mediaSession;
    if (!ms || typeof MediaMetadata === "undefined") return;
    ms.metadata = new MediaMetadata({
      title: state.episode.title,
      artist: "Fertility Talks",
      album: state.seasonNumber ? `Season ${state.seasonNumber}` : undefined,
    });
  }, [state.episode, state.seasonNumber]);
}
